<template>
  <article>
    <activity-stage :customRootContainerClass="`${keyboardEnabled ? '' : 'full-height'}`">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div v-if="!activityComplete" class="teacher-creation" transition="fade">
          <text-display-list :id="'textList'">
            <li
              v-for="(step, stepIndex) in currentRound.steps"
              :key="`tc_step-${stepIndex}`"
              :class="`step step-${stepIndex}`"
            >
              <blinking-cursor-text :text="step.answer" :text-step="stepIndex">
              </blinking-cursor-text>
            </li>
          </text-display-list>
        </div>
        <div v-if="showCongratsScreen" class="congrats-screen" transition="fade">
          <figure class="congrats">
            <img
              :src="assessmentCompletePath"
              alt="Group of stars to show teacher assessment completed."
            />
          </figure>
          <static-intro-button
            :enabled="enableButton"
            :on-click="handleRoundEnd"
          ></static-intro-button>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import StaticIntroButton from 'components/buttons/StaticIntroButton.vue';

import assessmentCompletePath from 'assets/images/assessment-complete.png';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList,
    StaticIntroButton
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'TeacherCreation',
      activityComplete: false,
      showCongratsScreen: false,
      assessmentCompletePath,
      enableButton: false
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const { rounds } = this.activityData;

      this.createRounds({
        rounds,
        instructions: {
          written: 'Type the text below.',
          voice: `${this.voiceInstructionsPrefix}/Type_the_text_below.mp3`
        }
      });
    },

    getKeyboardConfig() {
      return {
        hideNumberRow: false,
        characters: null
      };
    },

    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.teacher-creation');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 55}px)`;
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();
      this.enableKeyboard();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.setContainerHeight();
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.finishStep();
      }
    },

    finishStep() {
      this.completeStep();
      if (this.isEndOfRound) {
        this.activityComplete = true;
        this.hideKeyboard();
        this.sndBell();
        TweenMax.delayedCall(0.5, () => {
          this.setInstructions({ written: '' });
          this.showCongratsScreen = true;
          this.enableButton = true;
        });
      } else {
        this.setCurrentStep();
      }
    },

    handleRoundEnd() {
      this.completeActivity();
    }
  }
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.teacher-creation {
  display: flex;
  flex-flow: column nowrap;
  padding: 0 20px;
  overflow: hidden;

  figure {
    height: 175px;
    position: relative;

    img {
      height: 100%;
    }
  }

  #textList {
    list-style: none;
    -webkit-padding-start: 0;
    margin: 0;
    padding: 0;
    position: relative;
    top: 0;
  }
}

.congrats-screen {
  display: flex;
  align-items: center;
  justify-content: center;
}

.static-intro-button {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 11;
}

.fade-transition {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter,
.fade-leave {
  opacity: 0;
}
</style>
